import React from 'react';
import { Instagram, Heart, ExternalLink } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { contactData, instagramPosts } from '../utils/mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

export const InstagramFeed = () => {
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.3, once: true });
  const [gridRef, gridVisible] = useScrollAnimation({ threshold: 0.1, once: true });

  const handleInstagramClick = () => {
    window.open(`https://instagram.com/${contactData.instagram}`, '_blank');
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-rose-50/30 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-10 right-0 w-96 h-96 bg-pink-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 left-0 w-96 h-96 bg-rose-200/20 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div
          ref={titleRef}
          className={`text-center mb-12 transition-all duration-1000 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white px-6 py-2 rounded-full mb-4">
            <Instagram className="h-5 w-5" />
            <span className="font-semibold">@{contactData.instagram}</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Follow Our Journey
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
            Get daily inspiration and behind-the-scenes looks at our latest projects
          </p>
          <Button
            onClick={handleInstagramClick}
            className="bg-gradient-to-r from-pink-500 via-rose-500 to-orange-500 hover:from-pink-600 hover:via-rose-600 hover:to-orange-600 text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
          >
            <Instagram className="mr-2 h-5 w-5" />
            Follow on Instagram
            <ExternalLink className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>

        {/* Instagram Grid */}
        <div
          ref={gridRef}
          className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-5xl mx-auto"
        >
          {instagramPosts.map((post, index) => (
            <Card
              key={post.id}
              onClick={handleInstagramClick}
              className={`group relative overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer aspect-square ${
                gridVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
              }`}
              style={{ transitionDelay: gridVisible ? `${index * 100}ms` : '0ms' }}
            >
              <img
                src={post.image}
                alt={post.caption}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <div className="text-center text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Heart className="h-6 w-6 fill-white" />
                    <span className="font-bold text-lg">{post.likes}</span>
                  </div>
                  <Instagram className="h-8 w-8 mx-auto" />
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Note about real feed */}
        <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
          * These are sample posts. Connect your Instagram Business account to display your actual feed.
        </p>
      </div>
    </section>
  );
};
